/**
 * computeMarkerQuickEditFields.ts
 *
 * Shared quick-edit field resolution for a timeline marker. Used by BOTH the
 * Creator Studio left transcript (`LeftSidePanel`) and the Content Studio
 * clip-preview transcript (`ClipTranscriptBridge`) so the two surfaces gate
 * the Edit affordance on the same "has editable fields" rule — an overlay
 * with no `allowQuickEdit` controls (and no standalone text) has nothing to
 * edit, so Edit is disabled wherever it appears (PLAT-10120).
 *
 * Resolution order:
 *  1. Composition path — named `compositionControls` with `allowQuickEdit`
 *     text leaves (`extractCompositionQuickEditFields`).
 *  2. Standalone-text fallback — an overlay with no `allowQuickEdit` controls.
 *     (a) the marker id is itself a text-bearing top-level element; else
 *     (b) the marker id is a container whose editable text sits on a NESTED
 *         leaf — resolved WITH its index path so the save targets the
 *         top-level placement and walks down (a nested leaf id can't be
 *         looked up directly by `timeline.getElement`, so an empty path here
 *         silently failed to persist); else
 *     (c) a time-overlap pick across top-level siblings (the marker id can
 *         point at a non-editable sibling in a multi-element range).
 *     The synthesized field is flagged `isTranslatable: true` so the popover
 *     engages the pin write path under non-original caption languages
 *     (PLAT-10066); under original language it renders as a plain editable
 *     original-text row.
 */

import {
  collectTextElementsInTimeRange,
  findFirstTextLeafPath,
  findNodeInTree,
  hasNonEmptyTextProperty,
} from './compositionElementPath';
import { extractCompositionQuickEditFields } from './extractCompositionQuickEditFields';

import type { CompositionQuickEditField } from './extractCompositionQuickEditFields';

/** Minimal marker shape the field walk needs — id + time range (seconds). */
export interface QuickEditMarkerLike {
  id: string;
  time: number;
  endTime: number;
}

/**
 * Resolve the `targetId` the quick-edit popover writes through, given the
 * resolved field list and the surface's fallback element id.
 *
 * Two field shapes flow out of `computeMarkerQuickEditFields`:
 *   - **Composition-control field** — non-empty `elementPath`; the writer
 *     walks that path from the placement, so the target is the placement id
 *     (`fallbackId`, e.g. the marker / active-selection id).
 *   - **Synthesized standalone/nested text field** — empty `elementPath` and
 *     its own resolved `sourceElementId` (the leaf the iframe may have
 *     surfaced inside a group/composition). The save must target that leaf
 *     directly, NOT the placement, or `commandUpdateElement` writes to the
 *     wrong element.
 *
 * Shared by all three quick-edit surfaces (Creator Studio transcript +
 * canvas, Content Studio clip-preview transcript) so the invariant lives in
 * one place and can't drift between them.
 */
export function resolveQuickEditTargetId(
  fields: ReadonlyArray<CompositionQuickEditField>,
  fallbackId: string,
): string {
  const first = fields[0];
  return first && first.elementPath.length === 0
    ? first.sourceElementId
    : fallbackId;
}

export function computeMarkerQuickEditFields(
  topLevel: ReadonlyArray<Record<string, unknown>> | undefined,
  extraElementData: Record<string, unknown> | null,
  marker: QuickEditMarkerLike,
): CompositionQuickEditField[] {
  // Composition path first (named controls + `allowQuickEdit` leaves).
  const compositionFields = extractCompositionQuickEditFields({
    videoSourceElements: topLevel,
    extraElementData,
    compositionElementId: marker.id,
  });
  if (compositionFields.length > 0) return compositionFields;

  // Fallback: a text overlay with no `allowQuickEdit` controls — synthesize a
  // single editable field. The `elementPath` is what makes the save resolve:
  //   - EMPTY path  → the text element is TOP-LEVEL; the popover targets its
  //     id directly (`modifyCompositionProperty` looks it up via
  //     `timeline.getElement`, which only finds top-level ids).
  //   - NON-EMPTY path → the text lives on a NESTED leaf inside the marker's
  //     placement; the popover targets the PLACEMENT id and the writer walks
  //     the path down to the leaf. Without this, a nested leaf was synthesized
  //     with an empty path + the leaf's own id — which `timeline.getElement`
  //     can't resolve, so the save silently failed (edits never persisted).
  if (!Array.isArray(topLevel)) return [];

  let overlay: Record<string, unknown> | null = null;
  let elementPath: number[] = [];

  // (a) The marker id IS a text-bearing element (top-level text overlay).
  const direct = findNodeInTree(topLevel, marker.id, hasNonEmptyTextProperty);
  if (direct) {
    overlay = direct;
    elementPath = [];
  } else {
    // (b) The marker id is a container (composition / group) whose editable
    //     text sits on a nested leaf — find it AND its path from the placement
    //     so the save can walk down to it.
    const placement = findNodeInTree(topLevel, marker.id);
    const nested = placement ? findFirstTextLeafPath(placement) : null;
    if (nested) {
      overlay = nested.node;
      elementPath = nested.path;
    } else {
      // (c) Time-overlap fallback: the marker's range may host multiple
      //     top-level elements (arrow + text + image) and the marker id points
      //     at a non-editable one; pick the first text-bearing top-level
      //     sibling whose `[time, time+duration]` overlaps the range. These
      //     are top-level, so an empty path targets them directly.
      const candidates = collectTextElementsInTimeRange(
        topLevel,
        marker.time,
        marker.endTime,
      );
      if (candidates.length > 0) {
        overlay = candidates[0];
        elementPath = [];
      }
    }
  }

  if (!overlay) return [];
  const text = (overlay as { text?: unknown }).text;
  const name = (overlay as { name?: unknown }).name;
  if (typeof text !== 'string') return [];
  const overlayId =
    typeof (overlay as { id?: unknown }).id === 'string'
      ? (overlay as { id: string }).id
      : marker.id;
  return [
    {
      controlId: `overlay-${overlayId}`,
      // A synthesized fallback field has no authored control behind it, so it
      // is always a plain text row (PLAT-10913).
      controlType: 'text',
      controlLabel: typeof name === 'string' && name !== '' ? name : 'Text',
      compositionId: '',
      // For a nested leaf (`elementPath` non-empty) the writer targets the
      // placement (`marker.id`) and walks the path; `sourceElementId` stays
      // the resolved leaf id for identity / display. For a top-level element
      // (`elementPath` empty) the popover targets `sourceElementId` directly.
      sourceElementId: overlayId,
      sourceElementName:
        typeof name === 'string' && name !== '' ? name : 'Text',
      property: 'text',
      // PLAT-10066 — flag as translatable so the popover engages the pin write
      // path under non-original caption languages. Under original language the
      // row is treated as a plain editable original-text row regardless.
      // PLAT-10506 — a control-less overlay has no authored flag to read, so it
      // defaults to translatable (consistent with the text-field defaults rule
      // in `validateTranslationFlags`). There is no opt-out for a control-less
      // overlay in v1; explicit `allowTranslation: false` only exists on
      // authored controls, which flow through `extractCompositionQuickEditFields`.
      isTranslatable: true,
      originalValue: text,
      elementPath: elementPath as ReadonlyArray<number>,
    },
  ];
}
